import { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Music,
  Music2,
  Music4,
  Mic2,
  ExternalLink,
  FileText,
  Video,
  BookOpen,
  ChevronRight,
  PlayCircle,
  ListMusic,
  Share2,
  Copy,
  Check
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Musical keys/tones
const MUSICAL_KEYS = [
  "C", "Am",
  "C# / Db", "A#m / Bbm",
  "D", "Bm",
  "D# / Eb", "Cm",
  "E", "C#m / Dbm",
  "F", "Dm",
  "F# / Gb", "D#m / Ebm",
  "G", "Em",
  "G# / Ab", "Fm",
  "A", "F#m / Gbm",
  "A# / Bb", "Gm",
  "B", "G#m / Abm"
];

const initialFormState = {
  nome: "",
  link_letra: "",
  link_video: "",
  link_cifra: "",
  versao: "",
  autor: "",
  bpm: "",
  capotraste: "",
  observacoes: "",
  arranjos: "",
  versoes: [] // Array de versões com nome e tom
};

export default function SongsPage() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  const [viewingSong, setViewingSong] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // State for adding new version
  const [showAddVersion, setShowAddVersion] = useState(false);
  const [newVersionName, setNewVersionName] = useState("");
  const [newVersionTom, setNewVersionTom] = useState("");

  // Get public link for songs
  const getPublicLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/musicas-publico`;
  };

  const copyPublicLink = async () => {
    const link = getPublicLink();
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = link;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setLinkCopied(true);
      toast.success("Link copiado!", {
        description: "Envie para quem quiser ver as músicas"
      });
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (error) {
      // Show the link in a toast so user can copy manually
      toast.info("Link para compartilhar:", {
        description: link,
        duration: 10000
      });
    }
  };

  useEffect(() => {
    loadSongs();
  }, []);

  const loadSongs = async () => {
    try {
      const res = await axios.get(`${API}/songs`);
      setSongs(res.data);
    } catch (error) {
      toast.error("Erro ao carregar músicas");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que tem pelo menos uma versão
    if (!form.versoes || form.versoes.length === 0) {
      toast.error("Adicione pelo menos uma versão com tom");
      return;
    }
    
    try {
      const data = {
        ...form,
        bpm: form.bpm ? parseInt(form.bpm) : null,
        capotraste: form.capotraste ? parseInt(form.capotraste) : null,
        // Remove tom field as we now use versoes
        tom: form.versoes[0]?.tom || "" // Keep first version tom for backwards compatibility
      };
      
      if (editingSong) {
        await axios.put(`${API}/songs/${editingSong.id}`, data);
        toast.success("Música atualizada!");
      } else {
        await axios.post(`${API}/songs`, data);
        toast.success("Música criada!");
      }
      setDialogOpen(false);
      resetForm();
      loadSongs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar música");
    }
  };

  const resetForm = () => {
    setEditingSong(null);
    setForm(initialFormState);
    setShowAddVersion(false);
    setNewVersionName("");
    setNewVersionTom("");
  };

  const handleEdit = (song) => {
    setEditingSong(song);
    // If song has old tom field but no versoes, create a version from it
    let versoes = song.versoes || [];
    if (versoes.length === 0 && song.tom) {
      versoes = [{ id: Date.now().toString(), nome: "Original", tom: song.tom }];
    }
    setForm({
      nome: song.nome || "",
      link_letra: song.link_letra || "",
      link_video: song.link_video || "",
      link_cifra: song.link_cifra || "",
      versao: song.versao || "",
      autor: song.autor || "",
      bpm: song.bpm || "",
      capotraste: song.capotraste || "",
      observacoes: song.observacoes || "",
      arranjos: song.arranjos || "",
      versoes: versoes
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja excluir esta música?")) return;
    try {
      await axios.delete(`${API}/songs/${id}`);
      toast.success("Música excluída");
      loadSongs();
    } catch (error) {
      toast.error("Erro ao excluir música");
    }
  };

  const handleView = (song) => {
    setViewingSong(song);
    setViewDialogOpen(true);
  };

  const handleAddVersion = () => {
    if (!newVersionName.trim() || !newVersionTom) {
      toast.error("Preencha o nome e tom da versão");
      return;
    }
    
    const newVersion = {
      id: Date.now().toString(),
      nome: newVersionName.trim(),
      tom: newVersionTom
    };
    
    setForm(prev => ({
      ...prev,
      versoes: [...(prev.versoes || []), newVersion]
    }));
    
    setNewVersionName("");
    setNewVersionTom("");
    setShowAddVersion(false);
  };

  const handleRemoveVersion = (versionId) => {
    setForm(prev => ({
      ...prev,
      versoes: prev.versoes.filter(v => v.id !== versionId)
    }));
  };

  const filteredSongs = songs.filter(
    (song) =>
      song.nome?.toLowerCase().includes(search.toLowerCase()) ||
      song.autor?.toLowerCase().includes(search.toLowerCase())
  );

  // Get primary version/tom display
  const getPrimaryTom = (song) => {
    if (song.versoes && song.versoes.length > 0) {
      return song.versoes[0].tom;
    }
    return song.tom || "";
  };

  const getVersionsCount = (song) => {
    return song.versoes?.length || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="musicas-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Músicas</h1>
        <div className="flex items-center gap-2">
          {/* Share public link button */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={copyPublicLink}
            className="gap-2"
            data-testid="share-songs-button"
          >
            {linkCopied ? (
              <>
                <Check className="h-4 w-4 text-green-500" />
                <span className="hidden sm:inline">Copiado!</span>
              </>
            ) : (
              <>
                <Share2 className="h-4 w-4" />
                <span className="hidden sm:inline">Compartilhar</span>
              </>
            )}
          </Button>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-song-button">
            <Plus className="h-4 w-4 mr-2" />
            Adicionar
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder="Buscar música..."
          className="pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="search-songs"
        />
      </div>

      {/* Lista de músicas - Layout Moderno */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {filteredSongs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <ListMusic className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">Nenhuma música encontrada</p>
              <p className="text-sm text-muted-foreground mt-1">Adicione sua primeira música</p>
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {filteredSongs.map((song, index) => (
                <div 
                  key={song.id} 
                  className="group flex items-center gap-4 p-4 hover:bg-gradient-to-r hover:from-muted/50 hover:to-transparent cursor-pointer transition-all duration-200"
                  onClick={() => handleView(song)}
                  data-testid={`song-row-${song.id}`}
                >
                  {/* Thumbnail com gradiente dinâmico baseado no índice */}
                  <div className={`relative flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center shadow-lg transform group-hover:scale-105 transition-transform ${
                    index % 4 === 0 ? 'bg-gradient-to-br from-violet-500 to-purple-600' :
                    index % 4 === 1 ? 'bg-gradient-to-br from-blue-500 to-cyan-500' :
                    index % 4 === 2 ? 'bg-gradient-to-br from-emerald-500 to-teal-500' :
                    'bg-gradient-to-br from-orange-500 to-amber-500'
                  }`}>
                    {index % 3 === 0 ? (
                      <Music className="h-7 w-7 text-white drop-shadow" />
                    ) : index % 3 === 1 ? (
                      <Music2 className="h-7 w-7 text-white drop-shadow" />
                    ) : (
                      <Mic2 className="h-7 w-7 text-white drop-shadow" />
                    )}
                    {/* Play overlay on hover */}
                    <div className="absolute inset-0 rounded-xl bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <PlayCircle className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  
                  {/* Song Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                      {song.nome}
                    </h3>
                    <p className="text-sm text-muted-foreground truncate">
                      {song.autor || "Autor desconhecido"}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {getPrimaryTom(song) && (
                        <Badge variant="outline" className="text-xs px-2 py-0 h-5 font-medium">
                          {getPrimaryTom(song)}
                        </Badge>
                      )}
                      {song.bpm && (
                        <Badge variant="secondary" className="text-xs px-2 py-0 h-5">
                          {song.bpm} BPM
                        </Badge>
                      )}
                      {getVersionsCount(song) > 1 && (
                        <Badge className="text-xs px-2 py-0 h-5 bg-primary/10 text-primary hover:bg-primary/20">
                          {getVersionsCount(song)} versões
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Right side: Links Icons */}
                  <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    {song.link_cifra && (
                      <a href={song.link_cifra} target="_blank" rel="noopener noreferrer" title="Cifra">
                        <div className="w-9 h-9 rounded-lg bg-amber-500/10 hover:bg-amber-500 flex items-center justify-center transition-colors group/icon">
                          <BookOpen className="h-4 w-4 text-amber-600 group-hover/icon:text-white transition-colors" />
                        </div>
                      </a>
                    )}
                    {song.link_letra && (
                      <a href={song.link_letra} target="_blank" rel="noopener noreferrer" title="Letra">
                        <div className="w-9 h-9 rounded-lg bg-blue-500/10 hover:bg-blue-500 flex items-center justify-center transition-colors group/icon">
                          <FileText className="h-4 w-4 text-blue-600 group-hover/icon:text-white transition-colors" />
                        </div>
                      </a>
                    )}
                    {song.link_video && (
                      <a href={song.link_video} target="_blank" rel="noopener noreferrer" title="Vídeo">
                        <div className="w-9 h-9 rounded-lg bg-red-500/10 hover:bg-red-500 flex items-center justify-center transition-colors group/icon">
                          <Video className="h-4 w-4 text-red-600 group-hover/icon:text-white transition-colors" />
                        </div>
                      </a>
                    )}
                    
                    {/* Separator */}
                    <div className="w-px h-6 bg-border mx-1"></div>
                    
                    {/* Edit/Delete buttons */}
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="h-9 w-9 rounded-lg hover:bg-muted" 
                      onClick={() => handleEdit(song)} 
                      data-testid={`edit-song-${song.id}`}
                    >
                      <Edit className="h-4 w-4 text-muted-foreground" />
                    </Button>
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="h-9 w-9 rounded-lg hover:bg-destructive/10" 
                      onClick={() => handleDelete(song.id)} 
                      data-testid={`delete-song-${song.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Song Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Music className="h-5 w-5 text-purple-500" />
              {viewingSong?.nome}
            </DialogTitle>
          </DialogHeader>
          {viewingSong && (
            <div className="space-y-4 py-4">
              {viewingSong.autor && (
                <div>
                  <Label className="text-muted-foreground">Autor</Label>
                  <p className="font-medium">{viewingSong.autor}</p>
                </div>
              )}
              
              {/* Versions */}
              {viewingSong.versoes && viewingSong.versoes.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Versões</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {viewingSong.versoes.map((v, idx) => (
                      <Badge key={idx} variant="secondary">
                        {v.nome} - Tom: {v.tom}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-3 gap-4">
                {viewingSong.bpm && (
                  <div>
                    <Label className="text-muted-foreground">BPM</Label>
                    <p className="font-medium">{viewingSong.bpm}</p>
                  </div>
                )}
                {viewingSong.capotraste && (
                  <div>
                    <Label className="text-muted-foreground">Capotraste</Label>
                    <p className="font-medium">{viewingSong.capotraste}</p>
                  </div>
                )}
                {viewingSong.versao && (
                  <div>
                    <Label className="text-muted-foreground">Versão</Label>
                    <p className="font-medium">{viewingSong.versao}</p>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {viewingSong.link_letra && (
                  <a href={viewingSong.link_letra} target="_blank" rel="noopener noreferrer">
                    <Badge className="bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 cursor-pointer">
                      <FileText className="h-3 w-3 mr-1" />
                      Ver Letra
                    </Badge>
                  </a>
                )}
                {viewingSong.link_video && (
                  <a href={viewingSong.link_video} target="_blank" rel="noopener noreferrer">
                    <Badge className="bg-red-500/10 text-red-600 hover:bg-red-500/20 cursor-pointer">
                      <Video className="h-3 w-3 mr-1" />
                      Ver Vídeo
                    </Badge>
                  </a>
                )}
                {viewingSong.link_cifra && (
                  <a href={viewingSong.link_cifra} target="_blank" rel="noopener noreferrer">
                    <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20 cursor-pointer">
                      <BookOpen className="h-3 w-3 mr-1" />
                      Ver Cifra
                    </Badge>
                  </a>
                )}
              </div>

              {viewingSong.arranjos && (
                <div>
                  <Label className="text-muted-foreground">Arranjos</Label>
                  <p className="text-sm whitespace-pre-wrap">{viewingSong.arranjos}</p>
                </div>
              )}

              {viewingSong.observacoes && (
                <div>
                  <Label className="text-muted-foreground">Letra / Observações</Label>
                  <p className="text-sm whitespace-pre-wrap font-mono bg-muted/50 p-3 rounded-lg max-h-60 overflow-auto">
                    {viewingSong.observacoes}
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-4 border-t">
                <Button variant="outline" className="flex-1" onClick={() => handleEdit(viewingSong)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => setViewDialogOpen(false)}>
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Song Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingSong ? "Editar Música" : "Nova Música"}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  required
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  data-testid="song-nome"
                />
              </div>

              <div className="space-y-2">
                <Label>Autor</Label>
                <Input
                  value={form.autor || ""}
                  onChange={(e) => setForm({ ...form, autor: e.target.value })}
                  data-testid="song-autor"
                />
              </div>

              {/* Versions Section - Tons diferentes (OBRIGATÓRIO) */}
              <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Versões *</Label>
                    <p className="text-xs text-muted-foreground">Adicione pelo menos uma versão com o tom</p>
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowAddVersion(!showAddVersion)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
                
                {showAddVersion && (
                  <div className="flex gap-2 items-end p-3 bg-background rounded-lg border">
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs">Nome da Versão</Label>
                      <Input
                        placeholder="Ex: Original, Tom João, Feminino"
                        value={newVersionName}
                        onChange={(e) => setNewVersionName(e.target.value)}
                      />
                    </div>
                    <div className="w-32 space-y-1">
                      <Label className="text-xs">Tom</Label>
                      <Select value={newVersionTom} onValueChange={setNewVersionTom}>
                        <SelectTrigger>
                          <SelectValue placeholder="Tom" />
                        </SelectTrigger>
                        <SelectContent position="popper" className="max-h-[200px]">
                          {MUSICAL_KEYS.map((key) => (
                            <SelectItem key={key} value={key}>{key}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="button" size="icon" onClick={handleAddVersion}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                
                {(form.versoes && form.versoes.length > 0) ? (
                  <div className="space-y-2">
                    {form.versoes.map((versao) => (
                      <div key={versao.id} className="flex items-center justify-between p-2 bg-background rounded-lg border">
                        <div className="flex items-center gap-3">
                          <Music className="h-4 w-4 text-primary" />
                          <span className="font-medium">{versao.nome}</span>
                          <Badge variant="secondary">{versao.tom}</Badge>
                        </div>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon" 
                          className="text-destructive h-8 w-8"
                          onClick={() => handleRemoveVersion(versao.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-2 bg-yellow-500/10 rounded-lg">
                    Nenhuma versão adicionada. Adicione pelo menos uma versão.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>BPM</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="120"
                    value={form.bpm || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setForm({ ...form, bpm: value });
                    }}
                    data-testid="song-bpm"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Capotraste</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="0"
                    value={form.capotraste || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setForm({ ...form, capotraste: value });
                    }}
                    data-testid="song-capotraste"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Versão (descrição)</Label>
                <Input
                  placeholder="Original, Ao vivo, Acústico..."
                  value={form.versao || ""}
                  onChange={(e) => setForm({ ...form, versao: e.target.value })}
                  data-testid="song-versao"
                />
              </div>

              <div className="space-y-2">
                <Label>Link da Letra</Label>
                <Input
                  placeholder="https://..."
                  value={form.link_letra || ""}
                  onChange={(e) => setForm({ ...form, link_letra: e.target.value })}
                  data-testid="song-link-letra"
                />
              </div>

              <div className="space-y-2">
                <Label>Link do Vídeo</Label>
                <Input
                  placeholder="https://youtube.com/..."
                  value={form.link_video || ""}
                  onChange={(e) => setForm({ ...form, link_video: e.target.value })}
                  data-testid="song-link-video"
                />
              </div>

              <div className="space-y-2">
                <Label>Link da Cifra</Label>
                <Input
                  placeholder="https://cifraclub.com/..."
                  value={form.link_cifra || ""}
                  onChange={(e) => setForm({ ...form, link_cifra: e.target.value })}
                  data-testid="song-link-cifra"
                />
              </div>

              <div className="space-y-2">
                <Label>Arranjos</Label>
                <Textarea
                  placeholder="Notas sobre arranjos..."
                  value={form.arranjos || ""}
                  onChange={(e) => setForm({ ...form, arranjos: e.target.value })}
                  data-testid="song-arranjos"
                />
              </div>

              <div className="space-y-2">
                <Label>Letra / Observações</Label>
                <Textarea
                  placeholder="Letra da música ou observações..."
                  value={form.observacoes || ""}
                  onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                  data-testid="song-observacoes"
                  className="font-mono text-sm min-h-[200px] whitespace-pre-wrap"
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1" data-testid="save-song-button">
                  Salvar
                </Button>
              </div>
            </form>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
